import { useEffect, useState } from "react";
import { useSimoChat } from "../views/useSimoChat";
import { isSettingsTab, SettingsView, type SettingsTab } from "../views/SettingsView";
import { useDashboardData } from "./useDashboardData";
import { accentClass } from "./orbAccent";
import { TopBar, type Surface } from "./TopBar";
import { CommandBar } from "./CommandBar";
import { CosmicMissionScene, CosmicSessionGate } from "./CosmicMissionScene";
import { useKeyboardSafeViewport } from "../useKeyboardSafeViewport";
import { api } from "../api";
import { ownerLogoutNavigationState, ownerNavigationTarget, ownerSurfaceForSettingsTab, ownerSurfaceFromHash, type OwnerNavId } from "./ownerNavigation";
import { AgentOsStudio } from "./AgentOsStudio";
import { ControlPlaneSurface } from "./ControlPlaneSurface";
import { OwnerRunConsole } from "./OwnerRunConsole";
import { MissionPlanApprovalPanel } from "./MissionPlanApprovalPanel";
import { MissionAdmissionNotice, type AdmissionErgebnis } from "./MissionAdmissionNotice";
import { CommandPalette } from "./CommandPalette";

/**
 * SIMO Holographic Mission-Control-Dashboard (SIMO-HOLOGRAPHIC-DASHBOARD-SHELL-1).
 * Orb zentral, Live-Monitoring-Panels links/rechts, Top-Controls oben, Command
 * unten, Settings als Drawer. Reagiert sichtbar auf den Arbeitszustand: bei
 * thinking/acting tritt AgentGraph/Tools hervor, bei pending Approval das
 * Approval-Panel, bei error die Fehlerfläche. Gesamte Chat-/Voice-/Approval-
 * Logik kommt unverändert aus dem geteilten Hook `useSimoChat`.
 */
export function DashboardView(): JSX.Element {
  useKeyboardSafeViewport();
  const chat = useSimoChat();
  const data = useDashboardData();
  const initialSurface = (): Surface => {
    // OPNAV1 Slice 5 — Deep-Links: `#/operations`, `#/control/queue` …
    const ausHash = ownerSurfaceFromHash(typeof window === "undefined" ? "" : window.location.hash);
    if (ausHash) return ausHash;
    const raw = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("settings");
    return isSettingsTab(raw) ? ownerSurfaceForSettingsTab(raw) : "home";
  };
  const [surface, setSurface] = useState<Surface>(initialSurface);
  const [activeNav, setActiveNav] = useState<OwnerNavId>(initialSurface);
  const [systemsTab, setSystemsTab] = useState<SettingsTab>(() => {
    const raw = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("settings");
    return raw === "connectors" || raw === "permissions" || raw === "secrets" || raw === "models" ? raw : "connectors";
  });
  // SIMO-OWNER-SESSION-UI-REACHABILITY-1: die Einstellungsflaeche hatte bisher
  // nur "policy" fest verdrahtet. Dadurch war der Reiter "Geraete" zwar gebaut
  // und navigierbar deklariert, aber in der laufenden App nicht erreichbar --
  // die Tableiste rendert erst ab zwei sichtbaren Reitern.
  const [settingsTab, setSettingsTab] = useState<SettingsTab>(() => {
    const raw = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("settings");
    return raw === "sessions" ? raw : "policy";
  });
  const [loggingOut, setLoggingOut] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [contextAssistantOpen, setContextAssistantOpen] = useState(false);
  // OWNEXP Slice 9 — Post-Submit-Ergebnis des Mission Composers als eigene,
  // ehrliche Meldung (ADMITTED / COULD NOT BE ADMITTED) statt Chat-Fehler.
  const [admission, setAdmission] = useState<AdmissionErgebnis | null>(null);
  const composerSurface = surface === "home" || surface === "missions" || surface === "chat";
  const openDecisions = data.approvalInbox?.items.filter((item) => item.status === "pending").length ?? 0;
  const notificationCount = data.notificationCenterItems.length || data.notifications.length;
  const systemStatus = data.health?.ok === false ? "prüfen" : data.hasSessionToken ? "bereit" : "login nötig";

  useEffect(() => {
    const onOpenPalette = (): void => setCommandPaletteOpen(true);
    window.addEventListener("simo:open-palette", onOpenPalette);
    return () => window.removeEventListener("simo:open-palette", onOpenPalette);
  }, []);

  useEffect(() => {
    const onOpenAssistant = (): void => setContextAssistantOpen(true);
    window.addEventListener("simo:open-context-assistant", onOpenAssistant);
    return () => window.removeEventListener("simo:open-context-assistant", onOpenAssistant);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandPaletteOpen((open) => !open);
      }
      if (event.ctrlKey && event.code === "Space") {
        event.preventDefault();
        setContextAssistantOpen((open) => !open);
      }
      if (event.key === "Escape") {
        setCommandPaletteOpen(false);
        setContextAssistantOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  async function startOwnerMission(goal: string): Promise<void> {
    try {
      const result = await api.agentOrbMissionStart(goal);
      //_OWNEXP Slice 9: Das Submit-Ergebnis wird als eigene strukturierte
      //_Meldung gezeigt (MISSION ADMITTED mit Queue-Item/Wartegrund/Links
      //_bzw. MISSION COULD NOT BE ADMITTED mit Owner-Grund) — nie als
      //_Chat-Handoff maskiert, nie still verschluckt.
      setAdmission(result);
      if (result.ok) {
        chat.setInput("");
        chat.setError(null);
        setSurface("home");
        setActiveNav("home");
        await data.refresh();
      }
    } catch (error) {
      setAdmission({ ok: false, error: error instanceof Error ? error.message : "unreachable", reason: "network_error" });
    }
  }

  async function logout(): Promise<void> {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await api.logout();
      const reset = ownerLogoutNavigationState();
      setSurface(reset.surface);
      setActiveNav(reset.activeNav);
      await data.refresh();
    } finally {
      setLoggingOut(false);
    }
  }

  function navigateOwnerShell(id: OwnerNavId): void {
    const target = ownerNavigationTarget(id);
    setActiveNav(id);
    setSurface(target.surface);
    // OPNAV1 Slice 5 — Deep-Link/Back/Forward: Navigation schreibt den Hash;
    // hashchange navigiert zurück (Browser-Buttons funktionieren dadurch).
    if (typeof window !== "undefined" && window.location.hash !== `#/${id}`) {
      window.history.pushState(null, "", `#/${id}`);
    }
  }

  useEffect(() => {
    const onHashChange = (): void => {
      const ausHash = ownerSurfaceFromHash(window.location.hash);
      if (ausHash) {
        setActiveNav(ausHash);
        setSurface(ausHash);
      }
    };
    window.addEventListener("popstate", onHashChange);
    window.addEventListener("hashchange", onHashChange);
    return () => {
      window.removeEventListener("popstate", onHashChange);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  function navigateSettingsTab(tab: SettingsTab): void {
    const target = ownerSurfaceForSettingsTab(tab);
    if (target === "systems") setSystemsTab(tab);
    if (target === "settings") setSettingsTab(tab);
    navigateOwnerShell(target);
  }

  return (
    <div className={`simo-dashboard-shell h-full min-h-0 w-full min-w-0 overflow-x-hidden flex flex-col simo-grid-bg ${accentClass(chat.orb)}`}>
      <TopBar
        chat={chat}
        surface={surface}
        setSurface={setSurface}
        activeNav={activeNav}
        onNavigate={navigateOwnerShell}
        onOpenSettings={() => navigateOwnerShell("settings")}
        sessionActive={data.hasSessionToken}
        onLogout={logout}
        loggingOut={loggingOut}
        openDecisions={openDecisions}
        notificationCount={notificationCount}
        systemStatus={systemStatus}
        openGates={
          data.ownerGate?.state === "LIVE" ? data.ownerGate.openCount : null
        }
        onOpenGates={() => {
          navigateOwnerShell("control");
          window.location.hash = "#/control/gates";
        }}
        onOpenNotifications={() => {
          navigateOwnerShell("control");
          window.location.hash = "#/control/attention";
        }}
      />

      <main className="simo-dashboard-main flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto xl:overflow-hidden simo-thin-scroll">
        {surface === "home" ? (
          !data.hasSessionToken ? (
            <CosmicSessionGate data={data} />
          ) : (
            <CosmicMissionScene
              chat={chat}
              data={data}
              onOpenSettingsTab={navigateSettingsTab}
              onOpenAgentOs={() => navigateOwnerShell("agent-os")}
              onOpenOperations={() => navigateOwnerShell("operations")}
            />
          )
        ) : (
          <div className="h-full min-h-0 overflow-y-auto simo-thin-scroll">
            {surface === "missions" && <MissionPrimarySurface data={data} />}
            {surface === "chat" && <ChatPrimarySurface chat={chat} />}
            {surface === "operations" && <OperationsCenterSurface data={data} onOpenAgentOs={() => navigateOwnerShell("agent-os")} onOpenBrain={() => navigateOwnerShell("brain")} onOpenControlPlane={() => navigateOwnerShell("control")} />}
            {surface === "control" && <ControlPlaneSurface data={data} />}
            {surface === "agent-os" && <AgentOsStudio data={data} />}
            {surface === "routines" && <RoutineLegacySurface onOpenAgentOs={() => navigateOwnerShell("agent-os")} />}
            {surface === "brain" && <SharedBrainStudioSurface data={data} onOpenAdministration={() => navigateSettingsTab("memory")} />}
            {surface === "systems" && <SettingsView key={systemsTab} initialTab={systemsTab} tabs={["connectors", "permissions", "secrets", "models"]} title="Systeme" />}
            {surface === "settings" && <SettingsView key={settingsTab} initialTab={settingsTab} tabs={["policy", "sessions"]} title="Einstellungen" />}
          </div>
        )}
      </main>

      {composerSurface ? (
        <CommandBar
          chat={chat}
          data={data}
          disabled={!data.hasSessionToken}
          onStartOwnerMission={(goal) => void startOwnerMission(goal)}
        />
      ) : surface === "control" ? (
        /* OWNEXP F-32/Review: kein fixierter FAB auf der Control Plane — er
           überdeckte Inhalte (Queue-Suche/-Werte). Der Assistent ist über den
           CP-Header-Button erreichbar (simo:open-context-assistant). */
        <button type="button" className="hidden" aria-hidden="true" tabIndex={-1} />
      ) : (
        <button
          type="button"
          className="fixed bottom-5 right-5 z-40 rounded-full border border-jarvis-accent/50 bg-jarvis-accent px-4 py-3 text-sm font-semibold text-jarvis-bg shadow-[0_18px_44px_rgba(45,212,191,0.24)] transition hover:scale-[1.02]"
          onClick={() => setContextAssistantOpen(true)}
          aria-label="Kontextbezogenen SIMO-Assistenten öffnen"
        >
          SIMO fragen
        </button>
      )}

      {admission && (
        <MissionAdmissionNotice
          ergebnis={admission}
          onClose={() => setAdmission(null)}
          onOpenMission={(missionId) => {
            setAdmission(null);
            navigateOwnerShell("control");
            window.location.hash = "#/control/missions-runs";
            // Mission-Detail öffnet aus der Missions&Runs-Fläche (deep-link der
            // Control Plane bleibt Navigation — kein zweiter Wahrheitspfad).
            window.dispatchEvent(new CustomEvent("simo:open-mission", { detail: missionId }));
          }}
          onOpenQueue={() => {
            navigateOwnerShell("control");
            window.location.hash = "#/control/queue";
          }}
        />
      )}
      {commandPaletteOpen && (
        <CommandPalette
          onClose={() => setCommandPaletteOpen(false)}
          onNavigate={(id) => {
            navigateOwnerShell(id);
            setCommandPaletteOpen(false);
          }}
          onOpenMission={(missionId) => {
            setCommandPaletteOpen(false);
            navigateOwnerShell("control");
            window.location.hash = "#/control/missions-runs";
            window.dispatchEvent(new CustomEvent("simo:open-mission", { detail: missionId }));
          }}
        />
      )}
      {contextAssistantOpen && (
        <ContextAssistantOverlay
          surface={surface}
          onClose={() => setContextAssistantOpen(false)}
          onOpenChat={() => {
            navigateOwnerShell("chat");
            setContextAssistantOpen(false);
          }}
        />
      )}
    </div>
  );
}

function MissionPrimarySurface({ data }: { data: ReturnType<typeof useDashboardData> }): JSX.Element {
  const pending = data.approvalInbox?.items.filter((item) => item.status === "pending").length ?? 0;
  // OPNAV1 Slice 3 — kanonische Semantik: MISSION=missions.active,
  // RUN=control_plane_runs non-terminal. Chat-/Tool-Runs zaehlen NICHT als
  // Mission-Runs; fehlendes Read-Model zeigt UNAVAILABLE statt Fake-0.
  const semantics = data.controlPlaneSummary?.semantics ?? null;
  const aktiveRuns = semantics ? semantics.runs.nonTerminal : null;
  const aktiveMissionen = semantics ? semantics.missions.active : null;
  return (
    <section className="mx-auto flex min-h-full w-full max-w-6xl flex-col gap-4 p-4 sm:p-6" aria-label="Missionen">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Primärfläche</p>
        <h1 className="text-2xl font-semibold">Missionen</h1>
        <p className="text-sm text-jarvis-muted">Läufe, Owner-Gates und Evidence aus der gemeinsamen SIMO-Control-Plane.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="card p-4"><span className="text-xs text-jarvis-muted">Aktive Missionen</span><strong className="block text-2xl">{aktiveMissionen === null ? <span className="text-amber-300">UNAVAILABLE</span> : aktiveMissionen}</strong></div>
        <div className="card p-4"><span className="text-xs text-jarvis-muted">Aktive Runs (Control Plane)</span><strong className="block text-2xl">{aktiveRuns === null ? <span className="text-amber-300">UNAVAILABLE</span> : aktiveRuns}</strong></div>
        <div className="card p-4"><span className="text-xs text-jarvis-muted">Offene Freigaben</span><strong className="block text-2xl">{pending}</strong></div>
        <div className="card p-4"><span className="text-xs text-jarvis-muted">Evidence</span><strong className="block text-2xl">{data.gatedEvidence.length}</strong></div>
      </div>
      {data.controlPlaneSummaryError && (
        <div className="card border border-amber-300/30 p-3 text-xs text-amber-200" role="status">
          Kanonische Zähler nicht verfügbar: {data.controlPlaneSummaryError.message} — es werden keine Chat-Runs als Ersatz gezählt.
        </div>
      )}
      <div className="card min-h-0 flex-1 overflow-auto p-4">
        <h2 className="mb-3 text-sm font-semibold">Aktuelle Laufspur (Chat-/Agent-Runs — Konversation, keine Mission-Runs)</h2>
        {data.runs.length === 0 ? <p className="text-sm text-jarvis-muted">Kein Lauf aktiv. Eine neue Mission kann unten gestartet werden.</p> : data.runs.slice(0, 25).map((run) => (
          <div key={run.id} className="grid gap-1 border-b border-jarvis-border/50 py-3 last:border-0 sm:grid-cols-[minmax(0,1fr)_auto]">
            <div className="min-w-0">
              <strong className="block min-w-0 truncate text-sm">{ownerFacingMissionTitle(run)}</strong>
              <details className="mt-1 text-xs text-jarvis-muted">
                <summary className="cursor-pointer">Technische Laufdetails</summary>
                <p className="mt-1 break-words rounded-md border border-white/10 bg-black/20 px-2 py-1 font-mono text-[10px]">{run.request || run.id}</p>
              </details>
            </div>
            <span className="text-xs text-jarvis-muted">{run.status}</span>
          </div>
        ))}
      </div>

      {/* SIMO-MISSION-PLAN-REVIEW-AND-APPROVAL-1 (B01 Slice 3) — Owner sieht VOR
          jeder Ausführung, was SIMO als Mission-Plan vorschlägt, und entscheidet
          (Freigeben/Ablehnen/Überarbeitung anfordern/Stoppen). Eigener Abschnitt,
          bestehende Mission-Inhalte oben bleiben unverändert. */}
      <MissionPlanApprovalPanel />

      {/* SIMO-OWNER-RUN-CONSOLE-1 — Aufgabe ohne API-Kenntnisse starten, sehen was
          läuft, kritische Schritte einzeln freigeben. Eigener Abschnitt, bestehende
          Mission-Inhalte oben bleiben unverändert. */}
      <OwnerRunConsole />
    </section>
  );
}

export function ownerFacingMissionTitle(run: { id: string; request?: string | null }): string {
  const raw = String(run.request || run.id || "").trim();
  const quoted = raw.match(/(?:Label|Titel)\s+"([^"]+)"/i)?.[1];
  const marker = raw.match(/Marker\s+([A-Z0-9-]+)/i)?.[1];
  if (/audit_recent/i.test(raw)) return "Audit-Readback prüfen";
  if (/create_routine_draft/i.test(raw)) return `Routine-Draft vorbereiten${quoted ? `: ${quoted}` : ""}`;
  if (/work_queue_add/i.test(raw)) return `Work-Queue-Aufgabe anlegen${quoted ? `: ${quoted}` : ""}`;
  if (/Auto-Execution Proof/i.test(raw)) return `Auto-Execution Proof prüfen${marker ? `: ${marker}` : ""}`;
  if (/read-only/i.test(raw) && marker) return `Read-only Evidence prüfen: ${marker}`;
  return raw.length > 120 ? `${raw.slice(0, 117)}…` : raw;
}

export function ownerFacingQueueTitle(item: { id: string; title?: string | null }): string {
  const raw = String(item.title || item.id || "").trim();
  const chatVoiceMarker = raw.match(/(SIMO-CHAT-VOICE-LIVE-\d{14})/u)?.[1];
  const autoIntentMarker = raw.match(/(SIMO-FREE-INTENT-AUTO-\d{14})/u)?.[1];
  if (chatVoiceMarker && /Work Queue Intake/i.test(raw)) return `Chat-/Voice Auftrag aufnehmen: ${chatVoiceMarker}`;
  if (chatVoiceMarker && /Routine Draft/i.test(raw)) return `Routine-Draft vorbereiten: ${chatVoiceMarker}`;
  if (autoIntentMarker) return `Auto-Intent Readback prüfen: ${autoIntentMarker}`;
  if (/Work Queue Intake/i.test(raw)) return raw.replace(/Work Queue Intake/gi, "Auftrag aufnehmen").trim();
  if (/Routine Draft/i.test(raw)) return raw.replace(/Routine Draft/gi, "Routine-Draft").trim();
  return raw.length > 96 ? `${raw.slice(0, 93)}…` : raw;
}

type OperationsTopologyTone = "live" | "warn" | "danger" | "quiet";
type OperationsTopologyLane = "SIMO" | "Agenten" | "Provider" | "Queues" | "Gates" | "Evidence";

export interface OperationsTopologyNode {
  id: string;
  lane: OperationsTopologyLane;
  label: string;
  status: string;
  detail: string;
  tone: OperationsTopologyTone;
}

export interface OperationsTopologyInput {
  ownerMission?: {
    runId: string;
    goal: string;
    phase: string;
    status: string;
    children: Array<{ runId: string; status: string; phase: string; model: string | null; provider: string | null; eventCount: number; providerEventsObserved: boolean; toolEventsObserved: boolean }>;
    approvalIds: string[];
    eventCount: number;
    latestEventType: string | null;
  } | null;
  runs: Array<{ id: string; request?: string | null; status: string; runtime?: string | null; model?: string | null; roleId?: string | null }>;
  agents: Array<{ id: string; name: string; liveStatus: string; activeRunIds?: string[]; highestRiskLevel?: string }>;
  connectors: Array<{ id: string; name: string; reachable: boolean; state?: string }>;
  toolRuns: Array<{ id?: number; runId: string; toolId: string; status: string; durationMs: number | null }>;
  workQueueItems?: Array<{ id: string; title: string; status: string; assignedAgent: string; blockedReason: string | null }>;
  pendingApprovals: number;
  evidenceCount: number;
  hostCount: number;
  routineCount: number;
  errors: Record<string, string>;
}

function topologyTone(status: string): OperationsTopologyTone {
  const normalized = status.toLowerCase();
  if (/(error|failed|blocked|danger)/u.test(normalized)) return "danger";
  if (/(awaiting|pending|paused|degraded|needs|warn)/u.test(normalized)) return "warn";
  if (/(running|active|queued|in_progress|connected|ready|done|completed)/u.test(normalized)) return "live";
  return "quiet";
}

export function deriveOperationsTopology(input: OperationsTopologyInput): OperationsTopologyNode[] {
  const activeRuns = input.runs.filter((run) => run.status === "running" || run.status === "awaiting_approval");
  const nodes: OperationsTopologyNode[] = [];
  const mission = input.ownerMission;
  const primaryRun = activeRuns[0];

  nodes.push({
    id: mission?.runId ?? primaryRun?.id ?? "simo-idle",
    lane: "SIMO",
    label: "SIMO Conductor",
    status: mission?.status ?? primaryRun?.status ?? "idle",
    detail: mission?.goal ?? (primaryRun ? ownerFacingMissionTitle(primaryRun) : "Keine aktive Mission; Start im Mission Center."),
    tone: topologyTone(mission?.status ?? primaryRun?.status ?? "idle"),
  });

  for (const child of mission?.children ?? []) {
    nodes.push({
      id: child.runId,
      lane: "Agenten",
      label: child.provider ? `${child.provider}${child.model ? ` · ${child.model}` : ""}` : child.model ?? "Child Agent",
      status: child.status,
      detail: `${child.phase} · ${child.eventCount} Events · Provider:${child.providerEventsObserved ? "belegt" : "nicht belegt"} · Tools:${child.toolEventsObserved ? "belegt" : "nicht belegt"}`,
      tone: topologyTone(child.status),
    });
  }

  for (const agent of input.agents.filter((candidate) => candidate.liveStatus !== "idle" || (candidate.activeRunIds?.length ?? 0) > 0).slice(0, 8)) {
    nodes.push({
      id: `agent:${agent.id}`,
      lane: "Agenten",
      label: agent.name,
      status: agent.liveStatus,
      detail: `${agent.activeRunIds?.length ?? 0} aktive Runs · Risiko ${agent.highestRiskLevel ?? "nicht gemeldet"}`,
      tone: topologyTone(agent.liveStatus),
    });
  }

  const reachableProviders = input.connectors.filter((connector) => connector.reachable).length;
  nodes.push({
    id: "provider-availability",
    lane: "Provider",
    label: "Provider-Verfügbarkeit",
    status: `${reachableProviders}/${input.connectors.length}`,
    detail: input.connectors.slice(0, 4).map((connector) => `${connector.name}:${connector.state ?? (connector.reachable ? "connected" : "unavailable")}`).join(" · ") || "Kein Provider-Readback.",
    tone: input.connectors.length > 0 && reachableProviders === input.connectors.length ? "live" : "warn",
  });

  for (const tool of input.toolRuns.filter((candidate) => candidate.status === "running" || candidate.status === "queued").slice(0, 6)) {
    nodes.push({
      id: `tool:${tool.id ?? `${tool.runId}:${tool.toolId}`}`,
      lane: "Provider",
      label: tool.toolId,
      status: tool.status,
      detail: `Run ${tool.runId} · ${tool.durationMs == null ? "Laufzeit offen" : `${tool.durationMs} ms`}`,
      tone: topologyTone(tool.status),
    });
  }

  for (const item of (input.workQueueItems ?? []).filter((candidate) => candidate.status === "queued" || candidate.status === "in_progress" || candidate.status === "blocked").slice(0, 6)) {
    nodes.push({
      id: `queue:${item.id}`,
      lane: "Queues",
      label: ownerFacingQueueTitle(item),
      status: item.status,
      detail: item.blockedReason ? `Blocker: ${item.blockedReason}` : `Agent: ${item.assignedAgent}`,
      tone: topologyTone(item.status),
    });
  }

  nodes.push({
    id: "owner-gates",
    lane: "Gates",
    label: "Approvals",
    status: input.pendingApprovals > 0 ? "awaiting_owner" : "clear",
    detail: input.pendingApprovals > 0 ? `${input.pendingApprovals} Owner-Entscheidungen offen.` : "Kein Owner-Gate offen.",
    tone: input.pendingApprovals > 0 ? "warn" : "quiet",
  });

  const errorKeys = Object.keys(input.errors);
  nodes.push({
    id: "recovery",
    lane: "Gates",
    label: "Recovery",
    status: errorKeys.length > 0 ? "attention_required" : "ready",
    detail: errorKeys.length > 0 ? input.errors[errorKeys[0]!] ?? "Fehlerdetails nicht gemeldet." : "Keine Dashboard-Fehler im Readback.",
    tone: errorKeys.length > 0 ? "danger" : "quiet",
  });

  nodes.push({
    id: "hosts-routines",
    lane: "Evidence",
    label: "Hosts & Routinen",
    status: input.hostCount > 0 ? "observed" : "not_reported",
    detail: `${input.hostCount} Hosts · ${input.routineCount} Routinen`,
    tone: input.hostCount > 0 || input.routineCount > 0 ? "live" : "quiet",
  });
  nodes.push({
    id: "audit-evidence",
    lane: "Evidence",
    label: "Audit & Evidence",
    status: input.evidenceCount > 0 ? "recorded" : "empty",
    detail: `${input.evidenceCount} Evidence-Readbacks · Rohwerte ausgeschlossen`,
    tone: input.evidenceCount > 0 ? "live" : "quiet",
  });

  return nodes;
}

function operationsToneClass(tone: OperationsTopologyTone): string {
  if (tone === "live") return "border-emerald-300/30 bg-emerald-300/10 text-emerald-100";
  if (tone === "warn") return "border-amber-300/35 bg-amber-300/10 text-amber-100";
  if (tone === "danger") return "border-red-400/35 bg-red-400/10 text-red-100";
  return "border-white/10 bg-black/20 text-jarvis-text";
}

function OperationsTopologyGraph({ nodes }: { nodes: OperationsTopologyNode[] }): JSX.Element {
  const lanes: OperationsTopologyLane[] = ["SIMO", "Agenten", "Provider", "Queues", "Gates", "Evidence"];
  return (
    <div className="card p-4" aria-label="Operations Live-Ausführungsgraph">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Live-Ausführungsgraph</p>
          <h2 className="text-sm font-semibold">SIMO → Agenten → Provider → Gates → Evidence</h2>
          <p className="mt-1 max-w-4xl text-xs text-jarvis-muted">
            Leitstellenbild aus echten Readbacks: Mission, Child-Runs, aktive Agenten, Provider, Queues, Owner-Gates, Recovery und Evidence. Fehlende Daten werden als nicht gemeldet gezeigt.
          </p>
        </div>
        <span className="rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-xs text-jarvis-muted">{nodes.length} belegte Knoten</span>
      </div>
      <div className="mt-4 grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-[repeat(6,minmax(0,1fr))]">
        {lanes.map((lane) => {
          const laneNodes = nodes.filter((node) => node.lane === lane);
          return (
            <section key={lane} className="min-w-0 overflow-hidden rounded-xl border border-white/10 bg-jarvis-bg/35 p-3" aria-label={`Operations Lane ${lane}`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">{lane}</p>
              <div className="mt-3 grid gap-2">
                {laneNodes.length > 0 ? laneNodes.map((node) => (
                  <article key={node.id} className={`min-w-0 overflow-hidden rounded-xl border p-3 ${operationsToneClass(node.tone)}`}>
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <strong className="min-w-0 break-words text-sm leading-snug">{node.label}</strong>
                      <span className="shrink-0 rounded-md border border-current/20 px-1.5 py-0.5 text-[9px] uppercase tracking-wider opacity-80">{node.status.replace(/_/g, " ")}</span>
                    </div>
                    <p className="mt-2 line-clamp-4 break-words text-[11px] leading-relaxed opacity-80">{node.detail}</p>
                  </article>
                )) : (
                  <p className="rounded-lg border border-dashed border-white/10 bg-black/15 p-3 text-xs text-jarvis-muted">Keine belegten Knoten.</p>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function ChatPrimarySurface({ chat }: { chat: ReturnType<typeof useSimoChat> }): JSX.Element {
  return (
    <section className="mx-auto flex min-h-full w-full max-w-5xl flex-col gap-4 p-4 sm:p-6" aria-label="Chat">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Primärfläche</p>
        <h1 className="text-2xl font-semibold">Chat</h1>
        <p className="text-sm text-jarvis-muted">Auftrag und Rückmeldung bleiben in SIMO; Coding-Worktrees, Diffs und PRs gehören SimoT.</p>
      </div>
      <div ref={chat.scrollRef} className="card min-h-0 flex-1 overflow-auto p-4" aria-live="polite">
        {chat.history.length === 0 ? <p className="text-sm text-jarvis-muted">Noch keine Nachrichten in dieser Unterhaltung.</p> : chat.history.map((message, index) => (
          <article key={`${message.role}-${index}`} className="mb-3 rounded-lg border border-jarvis-border/50 bg-jarvis-bg/40 p-3 last:mb-0">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-jarvis-muted">{message.role === "user" ? "Owner" : "SIMO"}</div>
            <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function OperationsCenterSurface({
  data,
  onOpenAgentOs,
  onOpenBrain,
  onOpenControlPlane,
}: {
  data: ReturnType<typeof useDashboardData>;
  onOpenAgentOs: () => void;
  onOpenBrain: () => void;
  onOpenControlPlane: () => void;
}): JSX.Element {
  // OPNAV1 Slice 3 — "Aktive Missionen" waren vorher Chat-Runs (agent_runs).
  // Kanonisch: missions.active bzw. control_plane_runs non-terminal aus der
  // Control-Plane-Summary; die Chat-Run-Liste bleibt sichtbar, aber als
  // Konversation gelabelt. Fehlt das Summary: UNAVAILABLE statt Fake-0.
  const semantics = data.controlPlaneSummary?.semantics ?? null;
  const aktiveMissionen = semantics ? semantics.missions.active : null;
  const aktiveRuns = semantics ? semantics.runs.nonTerminal : null;
  const kanonischeRuns = semantics?.runs.activeList ?? [];
  const blockedQueue = data.workQueueItems?.filter((item) => item.status === "blocked").length ?? 0;
  const pendingApprovals = data.approvalInbox?.items.filter((item) => item.status === "pending").length ?? 0;
  const providerOnline = data.connectors.filter((connector) => connector.reachable).length;
  const errorCount = data.runs.filter((run) => run.status === "error").length + Object.keys(data.errors).length + blockedQueue;
  const activeTools = data.toolRuns.filter((tool) => tool.status === "running" || tool.status === "queued").length;
  const hosts = data.ownerDevices.length;
  const routineCount = data.routines?.length ?? data.overview?.routines.count ?? 0;
  const queueCount = data.workQueueItems?.filter((item) => item.status === "queued" || item.status === "in_progress").length ?? 0;
  const topology = deriveOperationsTopology({
    ownerMission: data.ownerMission,
    runs: data.runs,
    agents: data.agents,
    connectors: data.connectors,
    toolRuns: data.toolRuns,
    workQueueItems: data.workQueueItems,
    pendingApprovals,
    evidenceCount: data.gatedEvidence.length,
    hostCount: hosts,
    routineCount,
    errors: data.errors,
  });

  return (
    <section className="mx-auto flex min-h-full w-full max-w-[1500px] flex-col gap-4 p-4 sm:p-6" aria-label="Operations Center">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">SIMO Leitstelle</p>
          <h1 className="text-2xl font-semibold">Operations Center</h1>
          <p className="text-sm text-jarvis-muted">
            Laufende Missionen, Agenten, Provider, Queues, Blocker, Hosts, Routinen, Tool-Ausführungen und Evidence. Inbox-Signale sind nur noch Eingangssignale.
          </p>
        </div>
        <button className="topbar-control min-h-11" type="button" onClick={() => void data.refresh()}>Aktualisieren</button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <OpsMetric label="Aktive Missionen" value={aktiveMissionen === null ? <span className="text-amber-300">UNAVAILABLE</span> : aktiveMissionen} tone={aktiveMissionen && aktiveMissionen > 0 ? "live" : "quiet"} />
        <OpsMetric label="Aktive Runs (Control Plane)" value={aktiveRuns === null ? <span className="text-amber-300">UNAVAILABLE</span> : aktiveRuns} tone={aktiveRuns && aktiveRuns > 0 ? "live" : "quiet"} />
        <OpsMetric label="Entscheidungen" value={pendingApprovals} tone={pendingApprovals > 0 ? "warn" : "quiet"} />
        <OpsMetric label="Provider" value={`${providerOnline}/${data.connectors.length}`} tone={providerOnline === data.connectors.length ? "live" : "warn"} />
        <OpsMetric label="Queues / Leases" value={queueCount} tone={queueCount > 0 ? "live" : "quiet"} />
        <OpsMetric label="Fehler / Blocker" value={errorCount} tone={errorCount > 0 ? "danger" : "quiet"} />
      </div>
      {data.controlPlaneSummaryError && (
        <div className="card border border-amber-300/30 p-3 text-xs text-amber-200" role="status">
          Kanonische Zähler nicht verfügbar: {data.controlPlaneSummaryError.message} — Chat-Runs werden nicht als Ersatz gezählt.
        </div>
      )}

      <OperationsTopologyGraph nodes={topology} />

      <div className="grid min-h-0 gap-4 xl:grid-cols-[1.25fr_0.9fr]">
        <div className="card min-h-[360px] p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Live-Missionen &amp; Agenten (Control-Plane-Runs)</h2>
            <span className="text-xs text-jarvis-muted">{aktiveRuns === null ? "UNAVAILABLE" : `${aktiveRuns} aktiv`} · {data.agents.length} Agenten-Readbacks</span>
          </div>
          {kanonischeRuns.length === 0 ? (
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-lg border border-emerald-300/20 bg-emerald-300/10 p-4">
                <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-100/70">Bereit</p>
                <strong className="mt-2 block text-sm">Kein Run aktiv</strong>
                <p className="mt-1 text-xs leading-relaxed text-jarvis-muted">Die Leitstelle wartet auf die nächste Owner-Mission aus dem Mission Center.</p>
              </div>
              <div className="rounded-lg border border-white/10 bg-black/20 p-4">
                <p className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Nächster Schritt</p>
                <strong className="mt-2 block text-sm">Mission starten</strong>
                <p className="mt-1 text-xs leading-relaxed text-jarvis-muted">Neue Arbeit beginnt in SIMO; Agenten, Queues und Evidence erscheinen hier live.</p>
              </div>
              <button type="button" className="rounded-lg border border-white/10 bg-black/20 p-4 text-left transition hover:border-jarvis-accent/40" onClick={onOpenControlPlane}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Nächstes Queue-Item</p>
                <strong className="mt-2 block text-sm">Control Plane öffnen</strong>
                <p className="mt-1 text-xs leading-relaxed text-jarvis-muted">Queue, Supervisor (NEXT ELIGIBLE / WHY WAITING) und Leases — die operative Wahrheit.</p>
              </button>
            </div>
          ) : (
            <ul className="space-y-2">
              {kanonischeRuns.slice(0, 8).map((run) => (
                <li key={String(run.run_id)} className="rounded-lg border border-white/10 bg-black/20 p-3">
                  <div className="flex min-w-0 items-center justify-between gap-3">
                    <strong className="min-w-0 truncate font-mono text-xs">{String(run.run_id)}</strong>
                    <span className="rounded-md border border-jarvis-accent/25 px-2 py-1 text-[10px] uppercase tracking-wider text-jarvis-accent">{String(run.status)}</span>
                  </div>
                  <p className="mt-1 text-xs text-jarvis-muted">Quelle: {String(run.source_type)}/{String(run.source_id)}{run.mission_id ? ` · Mission ${String(run.mission_id)}` : ""}</p>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <OpsReadback title="Agenten & Subagenten" value={`${data.agentOsStudio?.summary.agents ?? data.agents.length} Agenten`} detail="Builder-Details im Agent OS Studio." action="Studio öffnen" onAction={onOpenAgentOs} />
            <OpsReadback title="Tool-Ausführungen" value={`${data.toolRuns.length} Tool-Runs`} detail={`${activeTools} aktuell aktiv oder queued.`} />
            <OpsReadback title="Kosten & Limits" value="nicht gemeldet" detail="Kein Kosten-/Limit-Readback im aktuellen Dashboard-State; wird nicht erfunden." />
          </div>
        </div>

        <div className="grid gap-4">
          <div className="card p-4">
            <h2 className="text-sm font-semibold">Provider, Hosts & Recovery</h2>
            <div className="mt-3 grid gap-2">
              <OpsReadback title="Provider-Verfügbarkeit" value={`${providerOnline}/${data.connectors.length} erreichbar`} detail={data.connectors.slice(0, 3).map((connector) => connector.name).join(" · ") || "Kein Connector-Readback."} />
              <OpsReadback title="Hosts" value={hosts > 0 ? `${hosts} Host-Geräte` : "nicht gemeldet"} detail={data.workerSandbox?.readOnlyByDefault ? "Worker-Sandbox read-only by default." : "Worker-/Host-Readback nicht vollständig gemeldet."} />
              <OpsReadback title="Recovery" value={errorCount > 0 ? "prüfen" : "kein Blocker offen"} detail={Object.values(data.errors).slice(0, 1)[0] ?? "Keine Dashboard-Fehler im Readback."} />
            </div>
          </div>

          <div className="card p-4">
            <h2 className="text-sm font-semibold">Inbox-Signale</h2>
            <p className="mt-1 text-xs text-jarvis-muted">PRs, Mails und Tickets bleiben Eingangssignale, nicht die Operations-Hauptfläche.</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              <OpsMetric label="PRs" value={data.overview?.runs.error ?? 0} tone="quiet" />
              <OpsMetric label="Tickets" value={data.notificationCenterItems.filter((item) => item.sourceKind === "system").length} tone="quiet" />
              <OpsMetric label="Mails" value={data.notifications.filter((item) => item.sourceKind === "system").length || data.notifications.length} tone="quiet" />
            </div>
          </div>

          <div className="card p-4">
            <h2 className="text-sm font-semibold">Brain & Routinen</h2>
            <div className="mt-3 grid gap-2">
              <OpsReadback title="Routinen" value={`${routineCount} konfiguriert`} detail={data.routineSchedulerEnabled ? "Scheduler aktiv." : "Scheduler nicht aktiv oder nicht gemeldet."} action="Routine-Builder" onAction={onOpenAgentOs} />
              <OpsReadback title="Audit / Evidence" value={`${data.gatedEvidence.length} Evidence`} detail={`${data.brainDecisionLog?.length ?? 0} Brain-Decisions im Readback.`} action="Brain öffnen" onAction={onOpenBrain} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

type SharedBrainStudioMode = "explorer" | "graph" | "learning" | "injection" | "versions";
type SharedBrainGraphTone = "live" | "warn" | "quiet";
type SharedBrainGraphKind = "truth" | "decision" | "lesson" | "scope" | "routine" | "hook" | "context" | "version";

interface SharedBrainGraphNode {
  id: string;
  kind: SharedBrainGraphKind;
  label: string;
  detail: string;
  tone: SharedBrainGraphTone;
}

interface SharedBrainGraphEdge {
  from: string;
  to: string;
  label: string;
}

export interface SharedBrainStudioGraphInput {
  memory: Array<{ key: string; space: string; kind: string; sensitivity: string; injectPolicy: string; reviewStatus: string; embeddingStatus: string; source: string | null }>;
  memoryTotal: number;
  decisions?: Array<{ id: number; summary: string; result: string; details?: { decisionType?: string; roleId?: string; space?: string; runId?: string; injectedCount?: number } }>;
  learning: Array<{ id: string; kind: string; targetKey: string; source: string; reason: string; status: string }>;
  scopes?: Array<{ role: { id: string; name: string }; scope: { allowedSpaces: string[]; allowedKinds: string[]; maxSensitivity: string; isDefault: boolean } }>;
  agentOsBrainAvailable: boolean;
  brainEntries: Array<{ id: string; provenance?: { joinStatus?: string }; version?: { drift?: string } }>;
  routines: Array<{ id: string; provenance?: { joinStatus?: string }; version?: { drift?: string } }>;
  hooks: Array<{ id: string; provenance?: { joinStatus?: string }; version?: { drift?: string } }>;
  evidence: Array<{ id: number; actionId: string; runId: string | null; resultStatus: string }>;
  registryRevision?: string | null;
  errors: Record<string, string>;
}

export interface SharedBrainStudioGraph {
  nodes: SharedBrainGraphNode[];
  edges: SharedBrainGraphEdge[];
  injectionPreview: {
    sourceCount: number;
    scopeCount: number;
    tokenBudget: string;
    excluded: string[];
    reason: string;
  };
  versions: {
    canonical: string;
    workingCopy: string;
    rollback: string;
  };
}

function safeBrainLabel(raw: string | null | undefined, fallback: string): string {
  const text = String(raw || "").trim();
  if (!text) return fallback;
  return text.length > 72 ? `${text.slice(0, 69)}…` : text;
}

export function deriveSharedBrainStudioGraph(input: SharedBrainStudioGraphInput): SharedBrainStudioGraph {
  const nodes: SharedBrainGraphNode[] = [];
  const edges: SharedBrainGraphEdge[] = [];
  const nodeIds = new Set<string>();
  const addNode = (node: SharedBrainGraphNode): void => {
    if (nodeIds.has(node.id)) return;
    nodeIds.add(node.id);
    nodes.push(node);
  };
  const addEdge = (edge: SharedBrainGraphEdge): void => {
    if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) return;
    edges.push(edge);
  };

  addNode({
    id: "project-truth",
    kind: "truth",
    label: "Project Truth",
    detail: input.agentOsBrainAvailable ? "Kanonischer Brain-Kontext im Agent OS Readback vorhanden." : "Project-Truth-Record nicht gemeldet.",
    tone: input.agentOsBrainAvailable ? "live" : "quiet",
  });
  addNode({
    id: "context-packet",
    kind: "context",
    label: "Context Packet",
    detail: `${input.scopes?.length ?? 0} Scopes · ${input.memoryTotal || input.memory.length} Memory-Metadaten · Rohwerte ausgeschlossen`,
    tone: (input.scopes?.length ?? 0) > 0 || input.memoryTotal > 0 ? "live" : "quiet",
  });

  for (const decision of (input.decisions ?? []).slice(0, 5)) {
    const id = `decision:${decision.id}`;
    addNode({
      id,
      kind: "decision",
      label: safeBrainLabel(decision.summary, `Decision ${decision.id}`),
      detail: `${decision.result} · ${decision.details?.decisionType ?? "Typ nicht gemeldet"}`,
      tone: decision.result === "blocked" || decision.result === "pending" ? "warn" : "live",
    });
    addEdge({ from: id, to: "project-truth", label: "Decision → Truth" });
    if (decision.details?.runId) addEdge({ from: id, to: "context-packet", label: "Run-Kontext" });
  }

  for (const proposal of input.learning.slice(0, 5)) {
    const id = `lesson:${proposal.id}`;
    addNode({
      id,
      kind: "lesson",
      label: safeBrainLabel(proposal.targetKey || proposal.kind, `Learning ${proposal.id}`),
      detail: `${proposal.kind} · ${proposal.status} · Quelle: ${proposal.source || "nicht gemeldet"}`,
      tone: proposal.status === "pending" ? "warn" : "live",
    });
    addEdge({ from: id, to: "project-truth", label: "Lesson → Truth" });
  }

  for (const scopeEntry of (input.scopes ?? []).slice(0, 5)) {
    const id = `scope:${scopeEntry.role.id}`;
    addNode({
      id,
      kind: "scope",
      label: scopeEntry.role.name,
      detail: `${scopeEntry.scope.allowedSpaces.join(", ") || "keine Spaces"} · Sensitivity bis ${scopeEntry.scope.maxSensitivity}`,
      tone: scopeEntry.scope.isDefault ? "quiet" : "live",
    });
    addEdge({ from: id, to: "context-packet", label: "Scope → Injection" });
  }

  if (input.routines.length > 0) {
    addNode({
      id: "routine-lane",
      kind: "routine",
      label: "Routinen",
      detail: `${input.routines.length} kanonische Routine-Records im Readback.`,
      tone: "live",
    });
    addEdge({ from: "routine-lane", to: "context-packet", label: "Routine → Context" });
  }

  if (input.hooks.length > 0) {
    addNode({
      id: "hook-lane",
      kind: "hook",
      label: "Hooks",
      detail: `${input.hooks.length} Hook-Records; Ausführung bleibt Agent-OS-gated.`,
      tone: "live",
    });
    if (nodeIds.has("routine-lane")) addEdge({ from: "routine-lane", to: "hook-lane", label: "Routine → Hook" });
  }

  if (input.brainEntries.length > 0) {
    addNode({
      id: "brain-working-copy",
      kind: "version",
      label: "Working Copy",
      detail: `${input.brainEntries.length} Brain-Records · ${input.brainEntries.filter((entry) => entry.version?.drift && entry.version.drift !== "in_sync").length} Drift`,
      tone: input.brainEntries.some((entry) => entry.version?.drift && entry.version.drift !== "in_sync") ? "warn" : "live",
    });
    addEdge({ from: "brain-working-copy", to: "project-truth", label: "Working-Copy-Diff" });
  }

  const sourceCount = input.memory.filter((entry) => entry.reviewStatus === "approved" && entry.injectPolicy !== "never").length;
  const excluded = [
    `${input.memory.filter((entry) => entry.sensitivity === "secret").length} Secret-Metadaten`,
    `${input.memory.filter((entry) => entry.reviewStatus !== "approved").length} nicht freigegebene Einträge`,
    `${input.memory.filter((entry) => entry.injectPolicy === "never").length} injectPolicy=never`,
  ];

  return {
    nodes,
    edges,
    injectionPreview: {
      sourceCount,
      scopeCount: input.scopes?.length ?? 0,
      tokenBudget: "nicht gemeldet",
      excluded,
      reason: sourceCount > 0 ? "Aus freigegebenen Memory-Metadaten und Agent-Brain-Scopes abgeleitet." : "Kein injizierbarer freigegebener Memory-Ausschnitt im aktuellen Readback.",
    },
    versions: {
      canonical: input.registryRevision ?? "nicht gebunden",
      workingCopy: input.brainEntries.length > 0 ? `${input.brainEntries.length} Records` : "nicht gemeldet",
      rollback: "Owner-gated",
    },
  };
}

const SHARED_BRAIN_MODES: Array<{ id: SharedBrainStudioMode; label: string; detail: string }> = [
  { id: "explorer", label: "Explorer", detail: "Suche, Tags, Kategorien, Scope." },
  { id: "graph", label: "Graph", detail: "Beziehungen aus Readbacks." },
  { id: "learning", label: "Learning", detail: "Proposal, Review, Canary." },
  { id: "injection", label: "Injection", detail: "Context Packet vor Runs." },
  { id: "versions", label: "Versions", detail: "Provenienz, Diff, Rollback." },
];

function SharedBrainStudioSurface({
  data,
  onOpenAdministration,
}: {
  data: ReturnType<typeof useDashboardData>;
  onOpenAdministration: () => void;
}): JSX.Element {
  const [brainMode, setBrainMode] = useState<SharedBrainStudioMode>("explorer");
  const missingEmbeddings = data.memory.filter((entry) => entry.embeddingStatus === "missing").length;
  const currentEmbeddings = data.memory.filter((entry) => entry.embeddingStatus === "current").length;
  const tags = Array.from(new Set(data.memory.map((entry) => entry.kind).filter(Boolean))).slice(0, 8);
  const graph = deriveSharedBrainStudioGraph({
    memory: data.memory,
    memoryTotal: data.memoryTotal,
    decisions: data.brainDecisionLog ?? [],
    learning: data.learningProposals,
    scopes: data.agentBrainScopes ?? [],
    agentOsBrainAvailable: Boolean(data.agentOsStudio?.brain),
    brainEntries: data.agentOsStudio?.brainEntries ?? [],
    routines: data.agentOsStudio?.routines ?? data.routines ?? [],
    hooks: data.agentOsStudio?.hooks ?? [],
    evidence: data.gatedEvidence,
    registryRevision: data.agentOsStudio?.registryRevision.revision ?? null,
    errors: data.errors,
  });

  return (
    <section className="mx-auto flex min-h-full w-full max-w-[1500px] flex-col gap-4 p-4 sm:p-6" aria-label="Shared Brain Studio">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Wissens- und Learning-Arbeitsraum</p>
          <h1 className="text-2xl font-semibold">Shared Brain Studio</h1>
          <p className="text-sm text-jarvis-muted">Explorer, Truth, Decisions, Lessons, Injection Preview, Provenienz, Versionen und Rollback — Maintenance liegt in Administration.</p>
          {/* OPNAV1 Slice 4 — Verfügbarkeitszustand ehrlich kennzeichnen:
              LIVE (Registry gebunden + Agent-Brain-Readback), PARTIAL (Daten
              da, kanonische Bindung fehlt), UNAVAILABLE (kein Read-Model).
              Nie still als vollständig/0 darstellen. */}
          <p className="mt-1 text-[11px]" data-testid="shared-brain-availability">
            {data.agentOsStudio
              ? data.agentOsStudio.registryRevision.status === "bound"
                ? <span className="text-emerald-300/90">Read-Model: LIVE (Revision {data.agentOsStudio.registryRevision.revision?.slice(0, 8) ?? "—"}{data.agentOsStudio.registryRevision.bindingMethod === "artifact_manifest" ? ", via Release-Manifest" : ""})</span>
                : <span className="text-amber-300">Read-Model: PARTIAL — Laufzeitdaten aktuell, kanonische Bindung nicht verifiziert ({data.agentOsStudio.registryRevision.bindingReason ?? "unbekannt"}).</span>
              : <span className="text-amber-300">Read-Model: UNAVAILABLE — {data.agentOsStudioError?.message ?? "kein Agent-OS-Readback"}.</span>}
          </p>
        </div>
        <button className="topbar-control min-h-11" type="button" onClick={onOpenAdministration}>Administration</button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" aria-label="Shared Brain Studio Arbeitsmodi">
        {SHARED_BRAIN_MODES.map((mode) => (
          <button
            key={mode.id}
            type="button"
            className={`min-h-[92px] rounded-xl border p-4 text-left transition hover:border-jarvis-accent/50 ${brainMode === mode.id ? "border-jarvis-accent/45 bg-jarvis-accent/12 shadow-[0_0_34px_rgba(45,212,191,0.12)]" : "border-white/10 bg-black/20"}`}
            aria-pressed={brainMode === mode.id}
            onClick={() => setBrainMode(mode.id)}
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">{mode.label}</p>
            <p className="mt-1 text-sm text-jarvis-text">{mode.detail}</p>
          </button>
        ))}
      </div>

      {brainMode === "explorer" && <SharedBrainExplorerWorkbench data={data} tags={tags} currentEmbeddings={currentEmbeddings} missingEmbeddings={missingEmbeddings} />}
      {brainMode === "graph" && <SharedBrainGraphWorkbench graph={graph} />}
      {brainMode === "learning" && <SharedBrainLearningWorkbench data={data} />}
      {brainMode === "injection" && <SharedBrainInjectionWorkbench graph={graph} scopes={data.agentBrainScopes ?? []} />}
      {brainMode === "versions" && <SharedBrainVersionsWorkbench graph={graph} data={data} onOpenAdministration={onOpenAdministration} />}
    </section>
  );
}

function SharedBrainExplorerWorkbench({
  data,
  tags,
  currentEmbeddings,
  missingEmbeddings,
}: {
  data: ReturnType<typeof useDashboardData>;
  tags: string[];
  currentEmbeddings: number;
  missingEmbeddings: number;
}): JSX.Element {
  return (
    <div className="grid min-h-0 gap-4 xl:grid-cols-[0.95fr_1.25fr_0.8fr]" aria-label="Shared Brain Explorer Workbench">
      <div className="card p-4">
        <h2 className="text-sm font-semibold">Explorer & Suche</h2>
        <input className="mt-3 w-full rounded-lg border border-jarvis-border bg-black/30 px-3 py-2 text-sm text-jarvis-text" placeholder="Suche nach Inhalt, Tag, Kategorie, Scope …" aria-label="Brain durchsuchen" />
        <div className="mt-3 grid gap-2 text-xs">
          <OpsReadback title="Inhalte" value={`${data.memoryTotal || data.memory.length} Memory-Metadaten`} detail="Redigierte Metadaten; Rohwerte bleiben ausgeschlossen." />
          <OpsReadback title="Kategorien / Tags" value={tags.length > 0 ? tags.join(" · ") : "nicht gemeldet"} detail="Aus vorhandenen Memory-Kinds abgeleitet." />
          <OpsReadback title="Scope" value={`${data.agentBrainScopes?.length ?? 0} Agent-Brain-Scopes`} detail="Injection-relevante Scope-Grenzen." />
        </div>
      </div>

      <div className="card min-h-[420px] p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">Project Truth, Decisions & Lessons</h2>
          <span className="text-xs text-jarvis-muted">{data.learningProposals.length} Learning-Proposals</span>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <OpsReadback title="Project Truth" value={data.agentOsStudio?.brain ? "verfügbar" : "nicht gemeldet"} detail="Truth wird als kanonischer Kontext geführt, nicht als Export-Textarea." />
          <OpsReadback title="Decisions" value={`${data.brainDecisionLog?.length ?? 0} Entscheidungen`} detail={data.brainDecisionLog?.[0]?.summary ?? "Kein Decision-Readback im aktuellen Snapshot."} />
          <OpsReadback title="Lessons" value={`${data.learningProposals.length} Vorschläge`} detail="Accept/Reject bleibt Owner-gated." />
          <OpsReadback title="Error Book" value={Object.keys(data.errors).length > 0 ? `${Object.keys(data.errors).length} Einträge` : "leer"} detail={Object.values(data.errors)[0] ?? "Keine Dashboard-Fehler im Readback."} />
        </div>
        <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
          <p className="text-xs font-semibold text-jarvis-text">Injection Preview</p>
          <p className="mt-1 text-xs text-jarvis-muted">Vor einem Run sichtbar: Quelle, Scope, Tokenbudget, Ausschlüsse und Begründung. Aktuell werden nur Metadaten angezeigt; keine Rohwerte.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <OpsMetric label="aktuell" value={currentEmbeddings} tone="quiet" />
            <OpsMetric label="ohne Embedding" value={missingEmbeddings} tone={missingEmbeddings > 0 ? "warn" : "quiet"} />
            <OpsMetric label="Evidence" value={data.gatedEvidence.length} tone="quiet" />
          </div>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="text-sm font-semibold">Structured Readback</h2>
        <div className="mt-3 grid gap-2">
          {data.memory.slice(0, 6).map((entry) => (
            <OpsReadback key={entry.key} title={entry.kind} value={`${entry.space} · ${entry.reviewStatus}`} detail={`${entry.injectPolicy} · ${entry.embeddingStatus} · Rohwert nicht geladen`} />
          ))}
          {data.memory.length === 0 && <OpsReadback title="Memory" value="nicht gemeldet" detail="Kein redigierter Memory-Readback im aktuellen Snapshot." />}
        </div>
      </div>
    </div>
  );
}

function sharedBrainToneClass(tone: SharedBrainGraphTone): string {
  if (tone === "live") return "border-emerald-300/30 bg-emerald-300/10 text-emerald-100";
  if (tone === "warn") return "border-amber-300/35 bg-amber-300/10 text-amber-100";
  return "border-white/10 bg-black/20 text-jarvis-text";
}

function SharedBrainGraphWorkbench({ graph }: { graph: SharedBrainStudioGraph }): JSX.Element {
  return (
    <div className="card p-4" aria-label="Shared Brain Graph Workbench">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Beziehungsgraph</p>
          <h2 className="text-lg font-semibold">Lesson → Agent · Decision → Policy · Routine → Hook · Run → Context Packet</h2>
          <p className="mt-1 max-w-4xl text-xs text-jarvis-muted">Graph aus vorhandenen Readbacks. Fehlende Beziehungen bleiben leer; SIMO erzeugt hier keine Fake-Kanten.</p>
        </div>
        <span className="rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-xs text-jarvis-muted">{graph.nodes.length} Knoten · {graph.edges.length} Beziehungen</span>
      </div>
      <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_0.8fr]">
        <div className="grid gap-3 md:grid-cols-2">
          {graph.nodes.map((node) => (
            <article key={node.id} className={`rounded-xl border p-4 ${sharedBrainToneClass(node.tone)}`}>
              <div className="flex items-start justify-between gap-3">
                <strong className="min-w-0 truncate text-sm">{node.label}</strong>
                <span className="shrink-0 rounded-md border border-current/20 px-2 py-1 text-[10px] uppercase tracking-wider">{node.kind}</span>
              </div>
              <p className="mt-2 text-xs leading-relaxed opacity-80">{node.detail}</p>
            </article>
          ))}
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <h3 className="text-sm font-semibold">Belegte Beziehungen</h3>
          <div className="mt-3 grid gap-2">
            {graph.edges.length > 0 ? graph.edges.map((edge) => (
              <div key={`${edge.from}:${edge.to}:${edge.label}`} className="rounded-lg border border-white/10 bg-jarvis-bg/35 p-3 text-xs text-jarvis-muted">
                <strong className="block text-jarvis-text">{edge.label}</strong>
                <span className="break-words">{edge.from} → {edge.to}</span>
              </div>
            )) : <p className="text-sm text-jarvis-muted">Keine belegten Graph-Kanten im aktuellen Readback.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SharedBrainLearningWorkbench({ data }: { data: ReturnType<typeof useDashboardData> }): JSX.Element {
  return (
    <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]" aria-label="Shared Brain Learning Workbench">
      <div className="card min-h-[420px] p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Learning Inbox</p>
            <h2 className="text-lg font-semibold">Proposal → Review → Canary → Accept/Reject</h2>
          </div>
          <span className="text-xs text-jarvis-muted">{data.learningProposals.length} Vorschläge</span>
        </div>
        <div className="mt-4 grid gap-3">
          {data.learningProposals.slice(0, 8).map((proposal) => (
            <article key={proposal.id} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                <strong className="min-w-0 truncate text-sm">{proposal.targetKey}</strong>
                <span className="rounded-md border border-amber-300/30 bg-amber-300/10 px-2 py-1 text-[10px] uppercase tracking-wider text-amber-100">{proposal.status}</span>
              </div>
              <p className="mt-2 text-xs text-jarvis-muted">{proposal.kind} · Quelle: {proposal.source || "nicht gemeldet"} · Review bleibt Owner-gated.</p>
            </article>
          ))}
          {data.learningProposals.length === 0 && <p className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-jarvis-muted">Keine Learning-Proposals im aktuellen Readback.</p>}
        </div>
      </div>
      <div className="card p-4">
        <h2 className="text-sm font-semibold">Review-Gates</h2>
        <div className="mt-3 grid gap-2">
          <OpsReadback title="Canary" value="Owner-gated" detail="SIMO zeigt Wirkung und Rollback-Pfad vor Accept." />
          <OpsReadback title="Accept/Reject" value="kein Direkt-Apply" detail="Learning wird nicht automatisch in Memory geschrieben." />
          <OpsReadback title="Error Book" value={Object.keys(data.errors).length > 0 ? `${Object.keys(data.errors).length} Einträge` : "leer"} detail={Object.values(data.errors)[0] ?? "Keine Dashboard-Fehler im Readback."} />
        </div>
      </div>
    </div>
  );
}

function SharedBrainInjectionWorkbench({
  graph,
  scopes,
}: {
  graph: SharedBrainStudioGraph;
  scopes: Array<{ role: { id: string; name: string }; scope: { allowedSpaces: string[]; allowedKinds: string[]; maxSensitivity: string; isDefault: boolean } }>;
}): JSX.Element {
  return (
    <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]" aria-label="Shared Brain Injection Workbench">
      <div className="card p-4">
        <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Injection Preview</p>
        <h2 className="text-lg font-semibold">Context Packet vor dem Run</h2>
        <p className="mt-2 text-sm text-jarvis-muted">Was injiziert wird, warum, aus welcher Quelle und was ausgeschlossen bleibt — ausschließlich als Metadaten.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <OpsMetric label="Quellen" value={graph.injectionPreview.sourceCount} tone={graph.injectionPreview.sourceCount > 0 ? "live" : "quiet"} />
          <OpsMetric label="Scopes" value={graph.injectionPreview.scopeCount} tone={graph.injectionPreview.scopeCount > 0 ? "live" : "quiet"} />
          <OpsMetric label="Tokenbudget" value={graph.injectionPreview.tokenBudget} tone="quiet" />
        </div>
      </div>
      <div className="card p-4">
        <h2 className="text-sm font-semibold">Scope & Ausschlüsse</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Warum</p>
            <p className="mt-2 text-xs text-jarvis-muted">{graph.injectionPreview.reason}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Ausgeschlossen</p>
            <ul className="mt-2 space-y-1 text-xs text-jarvis-muted">
              {graph.injectionPreview.excluded.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
          {scopes.slice(0, 4).map((scope) => (
            <OpsReadback key={scope.role.id} title={scope.role.name} value={scope.scope.allowedSpaces.join(" · ") || "keine Spaces"} detail={`Kinds: ${scope.scope.allowedKinds.join(" · ") || "nicht gemeldet"} · max ${scope.scope.maxSensitivity}`} />
          ))}
          {scopes.length === 0 && <OpsReadback title="Scopes" value="nicht gemeldet" detail="Kein Agent-Brain-Scope im aktuellen Readback." />}
        </div>
      </div>
    </div>
  );
}

function SharedBrainVersionsWorkbench({
  graph,
  data,
  onOpenAdministration,
}: {
  graph: SharedBrainStudioGraph;
  data: ReturnType<typeof useDashboardData>;
  onOpenAdministration: () => void;
}): JSX.Element {
  return (
    <div className="grid gap-4 xl:grid-cols-[1fr_0.9fr]" aria-label="Shared Brain Versions Workbench">
      <div className="card p-4">
        <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Provenienz & Versionen</p>
        <h2 className="text-lg font-semibold">Canonical · Runtime · Working-Copy-Diff</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <OpsReadback title="Canonical" value={graph.versions.canonical} detail="Registry-Revision aus Agent OS Readback." />
          <OpsReadback title="Working Copy" value={graph.versions.workingCopy} detail="Brain-Records aus kanonischer Projektion; Rohwerte ausgeschlossen." />
          <OpsReadback title="Runtime" value={`${data.memoryTotal || data.memory.length} Memory-Metadaten`} detail="Runtime-Metadaten, nicht der rohe Memory-Wert." />
          <OpsReadback title="Rollback" value={graph.versions.rollback} detail="Rollback wird nicht automatisch ausgelöst." />
        </div>
      </div>
      <div className="card p-4">
        <h2 className="text-sm font-semibold">Administration bleibt ausgelagert</h2>
        <p className="mt-2 text-sm text-jarvis-muted">Embedding, Export und Restore sind Maintenance-Funktionen und bleiben bewusst außerhalb der Brain-Hauptfläche.</p>
        <div className="mt-4 grid gap-2">
          <OpsReadback title="Embedding" value={`${data.memory.filter((entry) => entry.embeddingStatus === "current").length} aktuell`} detail={`${data.memory.filter((entry) => entry.embeddingStatus === "missing").length} ohne Embedding im Readback.`} />
          <OpsReadback title="Export / Restore" value="Administration" detail="Redacted-first; Full Export nur Owner/Admin mit expliziter Bestätigung." action="Öffnen" onAction={onOpenAdministration} />
        </div>
      </div>
    </div>
  );
}

function RoutineLegacySurface({ onOpenAgentOs }: { onOpenAgentOs: () => void }): JSX.Element {
  return (
    <section className="mx-auto flex min-h-full w-full max-w-5xl flex-col justify-center gap-4 p-4 sm:p-6" aria-label="Routinen Legacy-Hinweis">
      <div className="card border-jarvis-accent/35 p-6">
        <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Legacy-Fläche entfernt</p>
        <h1 className="mt-2 text-2xl font-semibold">Routinen laufen kanonisch im Agent OS Studio.</h1>
        <p className="mt-2 text-sm text-jarvis-muted">
          Bearbeitung, Testlauf, Diff, Freigabe und Apply gehören zu Agent OS Studio → Automation → Routinen. Diese Route bleibt nur als sicherer Expert-Hinweis erhalten.
        </p>
        <button className="mt-4 rounded-lg border border-jarvis-accent/50 bg-jarvis-accent/15 px-4 py-2 text-sm font-semibold text-jarvis-accent" type="button" onClick={onOpenAgentOs}>
          Agent OS Studio öffnen
        </button>
      </div>
    </section>
  );
}

function ContextAssistantOverlay({
  surface,
  onClose,
  onOpenChat,
}: {
  surface: Surface;
  onClose: () => void;
  onOpenChat: () => void;
}): JSX.Element {
  return (
    <div className="fixed bottom-20 right-5 z-50 w-[min(92vw,380px)] rounded-2xl border border-jarvis-accent/35 bg-jarvis-surface p-4 shadow-[0_24px_70px_rgba(0,0,0,0.45)]" role="dialog" aria-label="Kontextbezogener SIMO-Assistent">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Ctrl+Space</p>
          <h2 className="text-base font-semibold">SIMO fragen</h2>
        </div>
        <button className="text-jarvis-muted hover:text-jarvis-text" type="button" onClick={onClose} aria-label="Assistent schließen">×</button>
      </div>
      <p className="mt-2 text-sm text-jarvis-muted">Kontext: {surface}. Frag SIMO nach Erklärung, Auswirkung oder nächstem sicheren Schritt — der volle Composer bleibt im Mission Center.</p>
      <button className="mt-4 w-full rounded-lg border border-jarvis-accent/45 bg-jarvis-accent/15 px-3 py-2 text-sm font-semibold text-jarvis-accent" type="button" onClick={onOpenChat}>
        In Chat öffnen
      </button>
    </div>
  );
}

function OpsMetric({ label, value, tone }: { label: string; value: number | string | JSX.Element; tone: "quiet" | "live" | "warn" | "danger" }): JSX.Element {
  const toneClass = tone === "live" ? "border-emerald-300/25 bg-emerald-300/10" : tone === "warn" ? "border-amber-300/25 bg-amber-300/10" : tone === "danger" ? "border-red-300/25 bg-red-300/10" : "border-white/10 bg-black/20";
  return (
    <div className={`rounded-xl border p-4 ${toneClass}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">{label}</p>
      <strong className="mt-1 block text-2xl text-jarvis-text">{value}</strong>
    </div>
  );
}

function OpsReadback({ title, value, detail, action, onAction }: { title: string; value: string; detail: string; action?: string; onAction?: () => void }): JSX.Element {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">{title}</p>
          <strong className="mt-1 block truncate text-sm text-jarvis-text">{value}</strong>
        </div>
        {action && onAction ? (
          <button className="shrink-0 rounded-md border border-jarvis-accent/35 px-2 py-1 text-[10px] text-jarvis-accent" type="button" onClick={onAction}>
            {action}
          </button>
        ) : null}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-jarvis-muted">{detail}</p>
    </div>
  );
}
